import type {
  AdminAdjustPointsInput,
  AdminPointAccount,
  AdminPointAccountDetail,
  AdminPointAccountListResult,
  PointEntry,
} from "@yahshi/contracts";
import { adminAdjustPointsInputSchema, adminPointAccountListQuerySchema } from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { PointApi } from "./PointApi";

const now = () => new Date().toISOString();

type PointAppLookup = { name: string; code: string };

const demoApps: Record<string, PointAppLookup> = {
  app_yahshi_web: { name: "Yahshi Web", code: "yahshi-web" },
  app_yahshi_admin: { name: "Yahshi Admin", code: "yahshi-admin" },
};

const demoUsers: Record<string, { publicId: string; name: string }> = {
  "11111111-1111-4111-8111-111111111111": { publicId: "1001", name: "林默" },
  "33333333-3333-4333-8333-333333333333": { publicId: "1003", name: "周野" },
  "44444444-4444-4444-8444-444444444444": { publicId: "1004", name: "顾言" },
};

function entry(partial: Omit<PointEntry, "id"> & { id?: string }): PointEntry {
  return {
    id: partial.id ?? `pt_${partial.appId}_${partial.createdAt}`,
    appId: partial.appId,
    userId: partial.userId,
    type: partial.type,
    delta: partial.delta,
    balanceAfter: partial.balanceAfter,
    idempotencyKey: partial.idempotencyKey,
    note: partial.note,
    createdAt: partial.createdAt,
  };
}

export const seedAccounts: AdminPointAccountDetail[] = [
  {
    account: {
      appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "11111111-1111-4111-8111-111111111111", userPublicId: "1001", userName: "林默",
      balance: 5000, version: 2, updatedAt: "2026-09-16T02:18:00.000Z",
    },
    entries: [
      entry({ appId: "app_yahshi_web", userId: "11111111-1111-4111-8111-111111111111", type: "credit", delta: 5200, balanceAfter: 5200, idempotencyKey: "idem_demo_credit_001", note: "演示入账", createdAt: "2026-09-10T02:18:00.000Z" }),
      entry({ appId: "app_yahshi_web", userId: "11111111-1111-4111-8111-111111111111", type: "debit", delta: -200, balanceAfter: 5000, idempotencyKey: "idem_demo_debit_001", note: "演示扣减", createdAt: "2026-09-16T02:18:00.000Z" }),
    ],
  },
  {
    account: {
      appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "44444444-4444-4444-8444-444444444444", userPublicId: "1004", userName: "顾言",
      balance: 200, version: 1, updatedAt: "2026-09-12T04:12:00.000Z",
    },
    entries: [
      entry({ appId: "app_yahshi_web", userId: "44444444-4444-4444-8444-444444444444", type: "credit", delta: 200, balanceAfter: 200, idempotencyKey: "idem_demo_credit_002", note: "演示入账", createdAt: "2026-09-12T04:12:00.000Z" }),
    ],
  },
];

export function createDevelopmentPointApi(options: {
  getApp?: (id: string) => Promise<PointAppLookup>;
} = {}): PointApi {
  const records = structuredClone(seedAccounts);
  const resolveApp = async (appId: string): Promise<PointAppLookup> => {
    if (options.getApp) {
      const app = await options.getApp(appId).catch(() => {
        throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      });
      if (!app) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
      return app;
    }
    const app = demoApps[appId];
    if (!app) throw new M1ApiError("APP_NOT_FOUND", "应用不存在");
    return app;
  };

  const mutate = async (input: AdminAdjustPointsInput): Promise<AdminPointAccountDetail> => {
    const parsed = adminAdjustPointsInputSchema.parse(input);
    const app = await resolveApp(parsed.appId);
    const user = demoUsers[parsed.userId];
    if (!user) throw new M1ApiError("USER_NOT_FOUND", "用户不存在");
    const existingByKey = records.find((item) => item.account.appId === parsed.appId && item.entries.some((row) => row.idempotencyKey === parsed.idempotencyKey));
    if (existingByKey) {
      const previous = existingByKey.entries.find((row) => row.idempotencyKey === parsed.idempotencyKey)!;
      if (existingByKey.account.userId !== parsed.userId || previous.delta !== parsed.delta) {
        throw new M1ApiError("IDEMPOTENCY_CONFLICT", "幂等键已用于其他积分参数");
      }
      return structuredClone(existingByKey);
    }
    const index = records.findIndex((item) => item.account.appId === parsed.appId && item.account.userId === parsed.userId);
    const current = index >= 0 ? records[index]! : {
      account: {
        appId: parsed.appId, appName: app.name, appCode: app.code,
        userId: parsed.userId, userPublicId: user.publicId, userName: user.name,
        balance: 0, version: 0, updatedAt: now(),
      } satisfies AdminPointAccount,
      entries: [] as PointEntry[],
    };
    const nextBalance = current.account.balance + parsed.delta;
    if (nextBalance < 0) throw new M1ApiError("POINTS_INSUFFICIENT", "积分余额不足");
    const at = now();
    current.account = {
      ...current.account,
      appName: app.name,
      appCode: app.code,
      balance: nextBalance,
      version: current.account.version + 1,
      updatedAt: at,
    };
    current.entries.unshift(entry({
      appId: parsed.appId, userId: parsed.userId, type: "adjust", delta: parsed.delta,
      balanceAfter: nextBalance, idempotencyKey: parsed.idempotencyKey, note: parsed.note, createdAt: at,
    }));
    if (index >= 0) records[index] = current;
    else records.unshift(current);
    return structuredClone(current);
  };

  return {
    async list(query = {}): Promise<AdminPointAccountListResult> {
      const parsed = adminPointAccountListQuerySchema.parse(query);
      const keyword = parsed.keyword.trim().toLowerCase();
      const filtered = records.map((item) => item.account).filter((item) => {
        if (parsed.appId && item.appId !== parsed.appId) return false;
        const haystack = `${item.userName} ${item.userPublicId} ${item.appName} ${item.appCode}`.toLowerCase();
        return !keyword || haystack.includes(keyword);
      });
      const offset = (parsed.page - 1) * parsed.pageSize;
      return { items: filtered.slice(offset, offset + parsed.pageSize), total: filtered.length };
    },
    async get(appId, userId) {
      const value = records.find((item) => item.account.appId === appId && item.account.userId === userId);
      if (!value) throw new M1ApiError("POINTS_NOT_FOUND", "积分账户或兑换等级不存在");
      return structuredClone(value);
    },
    adjust: mutate,
  };
}
